import React, { useEffect, useState } from 'react';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { translate } from '../../../services/translate';
import { View } from 'react-native';
import { dispatchService } from '../../../services/central-api/dispatch';
import CustomButton from '../../../components/CustomButton';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../services/window-information';
import Map from '../../../components/Map';
import FormInput from '../../../components/FormInput';
import Toast from 'react-native-toast-message';
import { centralAPI } from '../../../services/central-api';
import LocationSelector from '../../../components/LocationSelector';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import { DispatchParamList } from '../../../typings/Params';

export default function PoliceStationForm({ navigation }: {
    navigation: Navigation;
}) {
    const route = useRoute<RouteProp<DispatchParamList, 'PoliceStationForm'>>();
    const policeStationId = route.params?.id;
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [pin, setPin] = useState<LatLng>();

    async function loadPageInfo() {
        try {
            if (policeStationId) {
                const policeStation = await dispatchService.getPoliceStation(policeStationId);

                if (policeStation) {
                    setName(policeStation.name);

                    if (policeStation.latitude && policeStation.longitude) {
                        setPin({ lat: policeStation.latitude, lng: policeStation.longitude });
                        fetchAddress({ lat: policeStation.latitude, lng: policeStation.longitude });
                    }
                }
            }
        } catch (err) {
            console.error(err);
        }
    }

    async function fetchAddress(pos: LatLng) {
        const address = await centralAPI.getAddressByPlace({ latitude: pos.lat, longitude: pos.lng });
        if (address) {
            setAddress(address);
        }
    }

    const isFocused = useIsFocused();
    useEffect(() => {
        if (isFocused) {
            loadPageInfo();
        }
    }, [route.params, isFocused]);

    function isEmpty(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isEmpty(name);
    }

    function cleanValues() {
        setName('');
        setAddress('');
        setPin({ lat: 0, lng: 0 });
    }

    async function formSubmit() {
        try {
            if (!pin || !pin.lat || !pin.lng) {
                return;
            }

            if (policeStationId) {
                await dispatchService.updatePoliceStation({
                    id: policeStationId,
                    name,
                    latitude: pin?.lat,
                    longitude: pin?.lng
                });
            } else {
                await dispatchService.createPoliceStation({
                    name,
                    latitude: pin?.lat,
                    longitude: pin?.lng
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            cleanValues();
            navigation.navigate('PoliceStations');
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('registerPoliceStation')}
                goBackAction={() => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('PoliceStations')}
                actions={
                    <CustomButton
                        text={translate('save')}
                        disabled={!isFormValid()}
                        onPress={formSubmit}
                    />
                }
            />

            <Card>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={[styles.leftArea, !windowInfo.isMobile ? { height: '100%' } : undefined]}>
                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            value={name}
                            labelFontSize={14}
                            onChangeText={setName}
                        />
                        <LocationSelector
                            address={address}
                            onAddressChange={setAddress}
                            label={translate('location')}
                            labelFontSize={14}
                            onPinChange={pos => setPin(pos)}
                        />
                    </View>

                    <View
                        style={[windowInfo.isMobile ? {
                            width: '100%',
                            height: '50%',
                        } : {
                            flex: 3,
                            height: '100%',
                        }]}>
                        <View style={styles.card(windowInfo.isMobile)}>
                            <Map
                                dynamicMarker={pin}
                                useDynamicMarker={true}
                                markerAsPoliceStation
                                markerChanged={(pos) => {
                                    fetchAddress(pos);
                                    setPin(pos);
                                }}
                            />
                        </View>
                    </View>
                </View>
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    leftArea: {
        borderWidth: 1,
        paddingHorizontal: 15,
        paddingVertical: 5,
        borderColor: theme.colors.card.border,
        overflowY: 'scroll',
        backgroundColor: theme.colors.card.background,
        borderRadius: 4,
        flex: 1,
        gap: 10,
    },
    card: (isMobile: boolean) => ({
        backgroundColor: theme.colors.card.background,
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 4,
        paddingVertical: 10,
        paddingHorizontal: 20,
        flex: 1,
        height: isMobile ? 200 : undefined
    }),
    formContainer: (isMobile: boolean) => ({
        flex: 1,
        flexDirection: isMobile ? 'column' : 'row',
        borderColor: theme.colors.container.border,
        gap: 16,
    }),
}));
